// Handles everything that depends on the *current page*:
// - reading JSON config emitted by PHP templates
// - album track active state + click-to-play

/**
 * Read a JSON config for the player from a <script type="application/json">.
 * Non-fatal on failure: logs and returns null.
 *
 * Expected in DOM (inside or near #audioPlayer):
 * <script type="application/json" id="audioPlayerConfig"> { ... } </script>
 */
export function readConfigFromJsonScript(rootEl) {
  if (!rootEl) return null;

  const scriptId = rootEl.dataset.playerConfigId || "audioPlayerConfig";

  // Prefer a script inside the root, but fall back to document-level by ID
  const localScript = rootEl.querySelector(`#${scriptId}`);
  const scriptEl = localScript || document.getElementById(scriptId);

  if (!scriptEl) return null;

  try {
    const jsonText = scriptEl.textContent || scriptEl.innerText || "";
    if (!jsonText.trim()) return null;
    return JSON.parse(jsonText);
  } catch (err) {
    console.error("[Player] Failed to parse JSON player config:", err);
    return null;
  }
}
/**
 * Merge config from DOM JSON + explicit config passed from JS.
 * Explicit config wins if both define the same property.
 */
export function buildAppConfig(rootEl, explicitConfig = {}) {
  const jsonConfig = readConfigFromJsonScript(rootEl) || {};
  return {
    ...jsonConfig,
    ...explicitConfig,
  };
}

export function initPageFromConfig() {
  if (
    window.SVPlayer &&
    typeof window.SVPlayer.configureFromPage === "function"
  ) {
    window.SVPlayer.configureFromPage();
  }
}

function clearAlbumTrackActiveStates() {
  const activeEls = document.querySelectorAll(".album-track--active");
  activeEls.forEach((el) => {
    el.classList.remove("album-track--active");
    el.removeAttribute("aria-current");
  });
}

function setAlbumTrackActiveByIndex(index) {
  clearAlbumTrackActiveStates();

  const el = document.querySelector(`[data-sv-track-index="${index}"]`);
  if (!el) return;

  el.classList.add("album-track--active");
  el.setAttribute("aria-current", "true");
}

/**
 * Sync the album track highlight (album-track--active) with whatever
 * is actually playing in the global player.
 *
 * - Called after clicks on album rows
 * - Called from syncNowPlayingUi() so next/prev and drawer clicks also update it
 */
export function syncAlbumActiveToCurrentAudio() {
  const SVPlayer = window.SVPlayer;
  if (!SVPlayer || typeof SVPlayer.getCurrentTrack !== "function") {
    return;
  }

  const track = SVPlayer.getCurrentTrack();
  const activeSlug = track
    ? String(
        track.slug || track.songSlug || track.id || track.drawerId || ""
      ).toLowerCase()
    : "";

  const albumLinks = document.querySelectorAll(
    ".album-track[data-sv-track-slug]"
  );

  // No current track → clear any highlights
  if (!activeSlug) {
    albumLinks.forEach((link) => {
      link.classList.remove("album-track--active");
      if (link.getAttribute("aria-current") === "true") {
        link.removeAttribute("aria-current");
      }
      const row = link.closest(".song-item, li");
      if (row) {
        row.classList.remove("is-current");
      }
    });
    return;
  }

  albumLinks.forEach((link) => {
    const linkSlug = String(
      link.getAttribute("data-sv-track-slug") || ""
    ).toLowerCase();

    const isCurrent = !!linkSlug && linkSlug === activeSlug;

    link.classList.toggle("album-track--active", isCurrent);

    if (isCurrent) {
      link.setAttribute("aria-current", "true");
    } else if (link.getAttribute("aria-current") === "true") {
      link.removeAttribute("aria-current");
    }

    const row = link.closest(".song-item, li");
    if (row) {
      row.classList.toggle("is-current", isCurrent);
    }
  });
}

// Expose for other code (and syncNowPlayingUi) to use
if (typeof window !== "undefined") {
  window.svSyncAlbumTrackUi = syncAlbumActiveToCurrentAudio;
}

export function initAlbumTrackClickPlayback() {
  const SVPlayer = window.SVPlayer;
  if (!SVPlayer) {
    return;
  }

  const getAlbumTracks = () =>
    typeof SVPlayer.getAlbumTracklist === "function"
      ? SVPlayer.getAlbumTracklist() || []
      : [];

  const getPlaylist = () =>
    typeof SVPlayer.getPlaylist === "function"
      ? SVPlayer.getPlaylist() || []
      : [];

  let albumTracks = getAlbumTracks();
  if (!Array.isArray(albumTracks)) {
    albumTracks = [];
  }

  let playlist = getPlaylist();
  if (!Array.isArray(playlist)) {
    playlist = [];
  }

  if (!albumTracks.length && !playlist.length) {
    // Even without a usable track list, keep album row highlight in sync.
    syncAlbumActiveToCurrentAudio();
    return;
  }

  const playButtons = document.querySelectorAll('[data-sv-play-button="true"]');
  if (!playButtons.length) {
    // Still sync highlight, even if there are no buttons
    syncAlbumActiveToCurrentAudio();
    return;
  }

  playButtons.forEach((btn) => {
    btn.addEventListener("click", (event) => {
      // Normal guards
      if (
        event.defaultPrevented ||
        event.button !== 0 || // not left-click
        event.metaKey || // cmd-click
        event.ctrlKey || // ctrl-click
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      event.preventDefault(); // stop the link navigation
      event.stopPropagation(); // don't let it bubble to the <a>

      const row = btn.closest("[data-sv-track-index]");
      if (!row) return;

      const rawIndex = row.getAttribute("data-sv-track-index");
      const index = parseInt(rawIndex, 10);
      if (!Number.isFinite(index) || index < 0) {
        return;
      }

      // Re-read the latest lists in case configureFromPage changed them
      let currentAlbumTracks = getAlbumTracks();
      if (!Array.isArray(currentAlbumTracks)) {
        currentAlbumTracks = [];
      }
      let currentPlaylist = getPlaylist();
      if (!Array.isArray(currentPlaylist)) {
        currentPlaylist = [];
      }

      // Prefer the per-page albumTracklist when it's available and matches this index.
      if (currentAlbumTracks.length && index < currentAlbumTracks.length) {
        const track = currentAlbumTracks[index];
        if (!track || !track.audioUrl) {
          console.warn(
            "[SVPlayer] No track/audioUrl for albumTracklist index",
            index,
            track
          );
          return;
        }

        if (typeof SVPlayer.loadPlaylist === "function") {
          SVPlayer.loadPlaylist(currentAlbumTracks, {
            startIndex: index,
            autoplay: true,
            load: true,
          });
        } else if (typeof SVPlayer.loadTrack === "function") {
          SVPlayer.loadTrack(track, { autoplay: true, reset: true });
        }

        //DEBUG
        svDebug("albumRowClick", {
          index,
          usingAlbumTracklist: !!(
            currentAlbumTracks && currentAlbumTracks.length
          ),
          trackId: track && track.id,
          slug: track && (track.slug || track.songSlug),
          audioUrl: track && track.audioUrl,
        });

        setAlbumTrackActiveByIndex(index);
        return;
      }

      // Fallback: use whatever autoPlaylist the player currently has
      if (currentPlaylist.length && index < currentPlaylist.length) {
        const track = currentPlaylist[index];
        if (!track || !track.audioUrl) {
          console.warn(
            "[SVPlayer] No track/audioUrl for playlist index",
            index,
            track
          );
          return;
        }

        if (typeof SVPlayer.loadTrack === "function") {
          SVPlayer.loadTrack(track, { autoplay: true, reset: true });
        }

        setAlbumTrackActiveByIndex(index);
      }
    });
  });

  // After wiring up clicks, sync the active state based on what is already playing
  syncAlbumActiveToCurrentAudio();
}